import produce, { Draft } from 'immer';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { NotificationData } from '../utils/notifications';
import { MarketOrderToast } from '../utils/notifications';

export type Notification = NotificationData | MarketOrderToast;

const DEFAULT_NOTIFICATIONS: Array<Notification> = [];

export interface NotificationStore {
	notifications: Array<Notification>;
	autoConfirmEnabled: boolean;
	set: (x: (s: NotificationStore) => void) => void;
	get: () => NotificationStore;
	addNotification: (notification: Notification) => void;
	clearAllNotifications: (
		predicate?: (notification: Notification) => boolean
	) => void;
	removeNotificationById: (notificationId: string) => void;
	removeNotificationByIndex: (index: number) => void;
	updateNotificationById: (
		notificationId: string,
		newNotification: Notification
	) => void;
	updateNotificationByIndex: (
		index: number,
		newNotification: Partial<Notification>
	) => void;
	updateOrAddNotificationById: (
		notificationId: string,
		newNotification: Notification,
		source?: string
	) => void;
	hideNotificationById: (notificationId: string) => void;
	setAutoConfirmEnabled: (enabled: boolean) => void;
}

const useNotificationStore = create(
	devtools<NotificationStore>((set, get): NotificationStore => {
		const immerSet = (fn: (s: NotificationStore) => void) => set(produce(fn));

		const addNotification: NotificationStore['addNotification'] = (
			notification
		) => {
			immerSet((s) => {
				s.notifications.unshift(notification);
			});
		};

		/**
		 * Inner utility method to remove notifications by index. Should be used inside of immerSet.
		 * @param s
		 * @param index
		 */
		const _removeNotificationByIndex = (
			s: Draft<NotificationStore>,
			index: number
		) => {
			s.notifications.splice(index, 1);
		};

		const removeNotificationById: NotificationStore['removeNotificationById'] =
			(notificationId) => {
				immerSet((s) => {
					const index = s.notifications.findIndex(
						(notification) => notification.id === notificationId
					);

					if (index !== -1) {
						_removeNotificationByIndex(s, index);
					}
				});
			};

		const removeNotificationByIndex: NotificationStore['removeNotificationByIndex'] =
			(index) => {
				immerSet((s) => {
					_removeNotificationByIndex(s, index);
				});
			};

		/**
		 * ⭐️ Should only be used inside of immerSet
		 * @param s
		 * @param index
		 * @param newNotification
		 */
		const _updateNotificationByIndex = (
			s: Draft<NotificationStore>,
			index: number,
			newNotification: Partial<Notification>
		) => {
			// @ts-ignore
			s.notifications[index] = {
				...s.notifications[index],
				...newNotification,
			};
		};

		const updateNotificationById: NotificationStore['updateNotificationById'] =
			(notificationId, newNotification) => {
				immerSet((s) => {
					const index = s.notifications.findIndex(
						(notification) => notification.id === notificationId
					);

					if (index !== -1) {
						_updateNotificationByIndex(s, index, newNotification);
					}
				});
			};

		const updateNotificationByIndex: NotificationStore['updateNotificationByIndex'] =
			(index, newNotification) => {
				immerSet((s) => {
					_updateNotificationByIndex(s, index, newNotification);
				});
			};

		const updateOrAddNotificationById: NotificationStore['updateOrAddNotificationById'] =
			(notificationId, newNotification, source) => {
				const index = get().notifications.findIndex(
					(notification) => notification.id === notificationId
				);

				const notificationToUpdate = {
					...newNotification,
					...(source &&
						index === -1 && {
							source,
						}),
					...(source &&
						index !== -1 &&
						!get().notifications[index].source && {
							source,
						}),
				};

				if (index !== -1) {
					updateNotificationByIndex(index, notificationToUpdate);
				} else {
					addNotification(notificationToUpdate);
				}
			};

		const hideNotificationById: NotificationStore['hideNotificationById'] = (
			notificationId
		) => {
			const notificationToUpdate = get().notifications.find(
				(n) => n.id === notificationId
			);

			if (notificationToUpdate) {
				updateNotificationById(notificationId, {
					...notificationToUpdate,
					hidden: true,
				});
			}
		};

		const clearAllNotifications: NotificationStore['clearAllNotifications'] = (
			predicate
		) => {
			immerSet((s) => {
				if (!predicate) {
					s.notifications = [];
				} else {
					s.notifications = s.notifications.filter((n) => !predicate(n));
				}
			});
		};

		const setAutoConfirmEnabled: NotificationStore['setAutoConfirmEnabled'] = (
			enabled
		) => {
			immerSet((s) => {
				s.autoConfirmEnabled = enabled;
			});
		};

		return {
			notifications: DEFAULT_NOTIFICATIONS,
			autoConfirmEnabled: false,
			set: immerSet,
			get: () => get(),
			addNotification,
			clearAllNotifications,
			removeNotificationById,
			removeNotificationByIndex,
			updateNotificationById,
			updateNotificationByIndex,
			updateOrAddNotificationById,
			hideNotificationById,
			setAutoConfirmEnabled,
		};
	})
);

export default useNotificationStore;
